// Privacy Policy Component
// Phase 11: Pre Go-Live Improvements
// © 2025 precatio, llc

import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="privacy-container">
      <header className="privacy-header">
        <button onClick={onBack} className="privacy-back-btn">← Back to Home</button>
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last Updated: January 11, 2026</p>
      </header>

      <main className="privacy-content">
        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            Precat.io ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, and safeguard your information when you use our spiritual 
            direction application and services.
          </p>
          <p>
            Precat.io is operated by precatio, llc. By using Precat.io, you agree to the collection and 
            use of information in accordance with this policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Information We Collect</h2>
          
          <h3>Account Information</h3>
          <p>
            When you create an account, we collect your email address and a securely hashed password. 
            We do not store your password in plain text.
          </p>

          <h3>Conversation Data</h3>
          <p>
            Your conversations with Iggy are stored to provide continuity in your spiritual journey. 
            This includes messages you send and responses from Iggy. This data is associated with your 
            account so you can resume conversations and track your spiritual growth over time.
          </p>

          <h3>Payment Information</h3>
          <p>
            If you subscribe to a paid tier, payment processing is handled securely by Stripe. We do not 
            store your full credit card number. We only receive confirmation of your subscription status 
            from Stripe.
          </p>

          <h3>Technical Data</h3>
          <p>
            We collect standard technical information such as IP addresses for security purposes (rate 
            limiting, fraud prevention) and basic usage analytics to improve our service.
          </p>
        </section>

        <section className="privacy-section">
          <h2>How We Use Your Information</h2>
          <ul>
            <li>To provide and maintain our spiritual direction services</li>
            <li>To personalize your experience and remember your spiritual journey</li>
            <li>To process subscriptions and payments</li>
            <li>To send important service-related communications</li>
            <li>To protect against fraud and abuse</li>
            <li>To improve our services</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>AI and Your Conversations</h2>
          <p>
            Iggy is powered by Claude, an AI assistant created by Anthropic. When you converse with Iggy:
          </p>
          <ul>
            <li>Your messages are sent to Anthropic's API to generate responses</li>
            <li>Anthropic's data retention and privacy policies also apply to this processing</li>
            <li>We store conversation history on our servers to maintain continuity</li>
            <li>Your conversations may be used to build a "spiritual profile" that helps Iggy remember 
                important aspects of your journey (this stays within Precat.io)</li>
          </ul>
          <p>
            We encourage you to review <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">
            Anthropic's Privacy Policy</a> for details on how they handle data.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Security</h2>
          <p>We implement industry-standard security measures including:</p>
          <ul>
            <li>Encrypted data transmission (HTTPS)</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Session token hashing</li>
            <li>Rate limiting to prevent abuse</li>
            <li>Account lockout after failed login attempts</li>
          </ul>
          <p>
            While we strive to protect your information, no method of electronic transmission or storage 
            is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Retention</h2>
          <p>
            We retain your account and conversation data for as long as your account is active. If you 
            wish to delete your account and all associated data, please contact us at the email below.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your conversation history</li>
            <li>Opt out of non-essential communications</li>
          </ul>
          <p>To exercise any of these rights, please contact us.</p>
        </section>

        <section className="privacy-section">
          <h2>Children's Privacy</h2>
          <p>
            Precat.io is not intended for children under 13. We do not knowingly collect personal 
            information from children under 13. If you believe we have collected information from a 
            child under 13, please contact us immediately.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Third-Party Services</h2>
          <p>We use the following third-party services:</p>
          <ul>
            <li><strong>Anthropic (Claude AI)</strong> - Powers Iggy's responses</li>
            <li><strong>Stripe</strong> - Payment processing</li>
            <li><strong>SendGrid</strong> - Email delivery</li>
            <li><strong>Google Cloud</strong> - Voice features (text-to-speech)</li>
          </ul>
          <p>Each of these services has their own privacy policies governing their use of data.</p>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant 
            changes by posting the new policy on this page and updating the "Last Updated" date.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or your data, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:privacy@precat.io">privacy@precat.io</a>
          </p>
          <p>
            <strong>Human Spiritual Direction:</strong> <a href="https://directionalfaith.com" target="_blank" rel="noopener noreferrer">DirectionalFaith.com</a>
          </p>
        </section>
      </main>

      <footer className="privacy-footer">
        <p>© 2025 precatio, llc</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
